/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Button,
  Spinner,
  FormGroup,
  FormFeedback,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { MultiSelect } from 'react-multi-select-component';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import DeleteModal from '../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';

const EMPTY_JETTY_FORM = {
  id: null,
  locationId: '',
  name: '',
  description: '',
  productIds: [],
  draftMin: '',
  draftMax: '',
  loaMin: '',
  loaMax: '',
  dwtMin: '',
  dwtMax: '',
  isActive: true,
};

const JettyLocationList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(false);

  const [modalJetty, setModalJetty] = useState(false);
  const [jettyFormData, setJettyFormData] = useState(EMPTY_JETTY_FORM);
  const [jettyErrors, setJettyErrors] = useState({});
  const [jettySaving, setJettySaving] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token, filterText);
    fetchLocations(token);
    fetchProducts(token);
  }, [currentPage, rowsPerPage, token, filterText]);

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    setCurrentPage(1);
  };

  const clearFilter = () => {
    setFilterText('');
    setCurrentPage(1);
  };

  const fetchData = async (currentPage, rowsPerPage, token, searchText) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      });
      if (searchText) {
        queryParams.append('Name', searchText);
      }

      const response = await fetch(
        `${api.API_URL}/location-jetty?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      const totalItems =
        result?.data?.totalItems || result?.totalItems || items.length;

      setData({
        items: Array.isArray(items) ? items : [],
      });
      setTotalRows(totalItems);
    } catch (error) {
      toast.error(`Failed to load data: ${error.message}`);
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async token => {
    try {
      const queryParams = new URLSearchParams({
        PageNumber: 1,
        PageSize: 300,
        SortBy: 'Name',
        IsDescending: false,
      });

      const response = await fetch(
        `${api.API_URL}/location?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      setLocationOptions(
        (Array.isArray(items) ? items : []).map(item => ({
          value: item.id,
          label: item.name,
        }))
      );
    } catch (error) {
      toast.error(`Failed to load locations: ${error.message}`);
      setLocationOptions([]);
    }
  };

  const fetchProducts = async token => {
    try {
      const response = await fetch(`${api.API_URL}/product`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      setProductOptions(
        (Array.isArray(items) ? items : []).map(item => ({
          value: item.id,
          label: item.name,
        }))
      );
    } catch (error) {
      toast.error(`Failed to load products: ${error.message}`);
      setProductOptions([]);
    }
  };

  const fetchJettyDetail = async id => {
    try {
      const response = await fetch(`${api.API_URL}/location-jetty/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const detail = result?.data ?? result;
      setJettyFormData({
        id: detail.id ?? id,
        locationId: detail.locationId || '',
        name: detail.name || '',
        description: detail.description || '',
        productIds: (detail.products || []).map(product => product.id),
        draftMin: detail.draftMin ?? '',
        draftMax: detail.draftMax ?? '',
        loaMin: detail.loaMin ?? '',
        loaMax: detail.loaMax ?? '',
        dwtMin: detail.dwtMin ?? '',
        dwtMax: detail.dwtMax ?? '',
        isActive: detail.isActive ?? true,
      });
    } catch (error) {
      toast.error(`Failed to fetch jetty details: ${error.message}`);
    }
  };

  const toNumberOrNull = value =>
    value === '' || value === null || value === undefined
      ? null
      : Number(value);

  const handleJettyNumberChange = field => e => {
    setJettyFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const validateJettyForm = () => {
    const nextErrors = {};
    if (!jettyFormData.locationId) {
      nextErrors.locationId = 'Location wajib dipilih.';
    }
    if (!jettyFormData.name?.trim()) {
      nextErrors.name = 'Jetty Name wajib diisi.';
    }
    if (!jettyFormData.productIds || jettyFormData.productIds.length === 0) {
      nextErrors.productIds = 'Product Name wajib dipilih minimal 1.';
    }
    setJettyErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNewModal = () => {
    setJettyFormData(EMPTY_JETTY_FORM);
    setJettyErrors({});
    setModalJetty(true);
  };

  const handleUpdateModal = id => {
    setJettyFormData(EMPTY_JETTY_FORM);
    setJettyErrors({});
    setModalJetty(true);
    fetchJettyDetail(id);
  };

  const handleDeleteModal = row => {
    setInvoice(row);
    setDeleteModal(true);
  };

  const confirmDeleteInvoice = async () => {
    if (!invoice) return;
    try {
      const response = await fetch(
        `${api.API_URL}/location-jetty/${invoice.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      toast.success('Jetty berhasil dihapus!', { autoClose: 3000 });
      setDeleteModal(false);
      setInvoice(null);
      fetchData(currentPage, rowsPerPage, token, filterText);
    } catch (err) {
      toast.error(`Gagal menghapus jetty: ${err.message}`, { autoClose: 3000 });
    }
  };

  const handleSaveJetty = async () => {
    if (!validateJettyForm()) return;

    const payload = {
      locationId: jettyFormData.locationId,
      name: jettyFormData.name,
      description: jettyFormData.description,
      productIds: jettyFormData.productIds,
      draftMin: toNumberOrNull(jettyFormData.draftMin),
      draftMax: toNumberOrNull(jettyFormData.draftMax),
      loaMin: toNumberOrNull(jettyFormData.loaMin),
      loaMax: toNumberOrNull(jettyFormData.loaMax),
      dwtMin: toNumberOrNull(jettyFormData.dwtMin),
      dwtMax: toNumberOrNull(jettyFormData.dwtMax),
      isActive: jettyFormData.isActive,
    };

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    setJettySaving(true);
    try {
      if (jettyFormData.id) {
        await axios.put(
          `${api.API_URL}/location-jetty/${jettyFormData.id}`,
          payload,
          config
        );
        toast.success('Jetty has been updated');
      } else {
        await axios.post(`${api.API_URL}/location-jetty`, payload, config);
        toast.success('Jetty has been added');
      }
      setModalJetty(false);
      fetchData(currentPage, rowsPerPage, token, filterText);
    } catch (error) {
      toast.error(`Failed to save jetty: ${error.message}`);
    } finally {
      setJettySaving(false);
    }
  };

  const columns = [
    {
      name: (
        <span className='form-label  text-uppercase fw-semibold fs-13'>
          Location Name
        </span>
      ),
      selector: row => row.locationName,
      sortable: true,
      width: '25%',
      wrap: true,
      cell: row => {
        return (
          <>
            <h6 className={'mb-0'}>{row.locationName || '-'}</h6>
          </>
        );
      },
    },

    {
      name: (
        <span className='form-label  text-uppercase fw-semibold fs-13'>
          Jetty Name
        </span>
      ),
      selector: row => row.name,
      sortable: true,
      width: '25%',
      wrap: true,
      cell: row => {
        return (
          <>
            <h6 className={'mb-0'}>{row.name || '-'}</h6>
          </>
        );
      },
    },

    {
      name: (
        <span className='form-label  text-uppercase fw-semibold fs-13'>
          Description
        </span>
      ),
      selector: row => row.description,
      sortable: true,
      width: '25%',
      wrap: true,
      cell: row => {
        return (
          <>
            <h6 className={'mb-0'}>{row.description || '-'}</h6>
          </>
        );
      },
    },

    {
      name: (
        <span className='form-label  text-uppercase fw-semibold fs-13'>
          Status
        </span>
      ),
      selector: row => row.isActive,
      sortable: true,
      width: '10%',
      wrap: true,
      cell: row => {
        return (
          <span
            className={`badge ${row.isActive ? 'bg-success' : 'bg-secondary'}`}
          >
            {row.isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },

    {
      name: (
        <span className='form-label  text-uppercase fw-semibold fs-13'>
          Action
        </span>
      ),
      width: '15%',
      sortable: false,
      className: 'text-end',
      cell: row => (
        <div className='d-flex gap-1 justify-content-end'>
          <button
            className='btn btn-soft-info btn-sm btn-icon'
            onClick={() => handleUpdateModal(row.id)}
          >
            <i className='ri-pencil-fill align-bottom'></i>
          </button>
          <button
            className='btn btn-soft-danger btn-sm btn-icon'
            onClick={() => handleDeleteModal(row)}
          >
            <i className='ri-delete-bin-6-line align-bottom'></i>
          </button>
        </div>
      ),
    },
  ];

  document.title = 'Data Jetty Location';

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => confirmDeleteInvoice()}
          onCloseClick={() => {
            setDeleteModal(false);
            setInvoice(null);
          }}
        />
        <Container fluid>
          <BreadCrumb title='View Data' pageTitle='Jetty Location' />

          <Row>
            <Col lg={12}>
              <Card id='JettyLocationList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Data Jetty Location
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <button
                          type='button'
                          className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                          onClick={() => handleNewModal()}
                        >
                          <i className='ri-play-list-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                          <span className='d-none d-sm-inline'>
                            Create New Jetty Location
                          </span>
                          <span className='d-sm-none'>New</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Filter Section */}
                  <div className='d-flex justify-content-between align-items-center mt-3'>
                    <div className='d-flex align-items-center gap-2'>
                      <Input
                        type='text'
                        placeholder='Search jetty locations...'
                        value={filterText}
                        onChange={handleFilterChange}
                        style={{ width: '300px' }}
                        className='form-control-sm'
                      />
                      {filterText && (
                        <Button
                          color='secondary'
                          size='sm'
                          onClick={clearFilter}
                          outline
                        >
                          <i className='ri-close-line'></i> Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={data.items}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      showPaginationBottom={true}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      fixedHeader={false}
                      fixedHeaderScrollHeight={undefined}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          {filterText
                            ? 'No jetty locations match the current filter'
                            : 'No jetty locations found'}
                        </div>
                      }
                      defaultSortFieldId={1}
                      defaultSortAsc={true}
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                            tableLayout: 'fixed',
                          },
                        },
                        responsiveWrapper: {
                          style: {
                            overflowX: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                          },
                        },
                        pagination: {
                          style: {
                            borderTop: '1px solid #dee2e6',
                            backgroundColor: '#f8f9fa',
                            margin: 0,
                            padding: '8px 0',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Modal Add / Edit Jetty */}
          <Modal
            isOpen={modalJetty}
            role='dialog'
            autoFocus={true}
            centered
            id='addJettyModal'
            toggle={() => setModalJetty(!modalJetty)}
            data-bs-backdrop='static'
            size='lg'
            zIndex={1060}
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalJetty(false)}
            >
              <span className='modal-title text-info fw-bold'>
                {jettyFormData.id ? 'Edit Jetty' : 'Add Jetty'}
              </span>
            </ModalHeader>

            <ModalBody>
              <div className='d-flex mt-2'>
                <Col sm={12}>
                  <Row>
                    <Col sm={6}>
                      <div className='mb-3'>
                        <Label for='jettyLocation' className='form-label'>
                          Location :
                        </Label>
                        <Select
                          id='jettyLocation'
                          options={locationOptions}
                          value={
                            locationOptions.find(
                              opt => opt.value === jettyFormData.locationId
                            ) || null
                          }
                          onChange={selected => {
                            setJettyFormData(prev => ({
                              ...prev,
                              locationId: selected?.value || '',
                            }));
                            setJettyErrors(prev => ({
                              ...prev,
                              locationId: '',
                            }));
                          }}
                          placeholder='Select Location'
                          isSearchable
                          isClearable
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: jettyErrors.locationId
                                ? '#dc3545'
                                : baseStyles.borderColor,
                              boxShadow: jettyErrors.locationId
                                ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
                                : baseStyles.boxShadow,
                            }),
                          }}
                        />
                        {jettyErrors.locationId && (
                          <div className='invalid-feedback d-block'>
                            {jettyErrors.locationId}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className='mb-3'>
                        <Label for='jettyName' className='form-label'>
                          Jetty Name :
                        </Label>
                        <Input
                          type='text'
                          id='jettyName'
                          name='name'
                          className='form-control'
                          value={jettyFormData.name}
                          invalid={!!jettyErrors.name}
                          onChange={e => {
                            setJettyFormData(prev => ({
                              ...prev,
                              name: e.target.value,
                            }));
                            setJettyErrors(prev => ({ ...prev, name: '' }));
                          }}
                        />
                        <FormFeedback>{jettyErrors.name}</FormFeedback>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='jettyProducts' className='form-label'>
                          Product Name :
                        </Label>
                        <div
                          style={{
                            border: jettyErrors.productIds
                              ? '1px solid #dc3545'
                              : undefined,
                            borderRadius: '0.375rem',
                          }}
                        >
                          <MultiSelect
                            options={productOptions}
                            value={productOptions.filter(option =>
                              jettyFormData.productIds.includes(option.value)
                            )}
                            onChange={selected => {
                              setJettyFormData(prev => ({
                                ...prev,
                                productIds: (selected || []).map(
                                  item => item.value
                                ),
                              }));
                              setJettyErrors(prev => ({
                                ...prev,
                                productIds: '',
                              }));
                            }}
                            labelledBy='Select Product'
                          />
                        </div>
                        {jettyErrors.productIds && (
                          <div className='invalid-feedback d-block'>
                            {jettyErrors.productIds}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label
                          htmlFor='jettyDescription'
                          className='form-label'
                        >
                          Description
                        </Label>
                        <textarea
                          className='form-control'
                          id='jettyDescription'
                          name='description'
                          rows='3'
                          value={jettyFormData.description}
                          onChange={e =>
                            setJettyFormData(prev => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                        ></textarea>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={4}>
                      <div className='mb-2'>
                        <Label className='form-label'>Draft(m)</Label>
                        <div className='input-group mb-2'>
                          <span className='input-group-text'>Min</span>
                          <Input
                            type='number'
                            name='draftMin'
                            id='jettyDraftMin'
                            value={jettyFormData.draftMin}
                            onChange={handleJettyNumberChange('draftMin')}
                          />
                          <span className='input-group-text'>m</span>
                        </div>
                        <div className='input-group'>
                          <span className='input-group-text'>Max</span>
                          <Input
                            type='number'
                            name='draftMax'
                            id='jettyDraftMax'
                            value={jettyFormData.draftMax}
                            onChange={handleJettyNumberChange('draftMax')}
                          />
                          <span className='input-group-text'>m</span>
                        </div>
                      </div>
                    </Col>
                    <Col sm={4}>
                      <div className='mb-2'>
                        <Label className='form-label'>LOA(m)</Label>
                        <div className='input-group mb-2'>
                          <span className='input-group-text'>Min</span>
                          <Input
                            type='number'
                            name='loaMin'
                            id='jettyLoaMin'
                            value={jettyFormData.loaMin}
                            onChange={handleJettyNumberChange('loaMin')}
                          />
                          <span className='input-group-text'>m</span>
                        </div>
                        <div className='input-group'>
                          <span className='input-group-text'>Max</span>
                          <Input
                            type='number'
                            name='loaMax'
                            id='jettyLoaMax'
                            value={jettyFormData.loaMax}
                            onChange={handleJettyNumberChange('loaMax')}
                          />
                          <span className='input-group-text'>m</span>
                        </div>
                      </div>
                    </Col>
                    <Col sm={4}>
                      <div className='mb-2'>
                        <Label className='form-label'>DWT(MT)</Label>
                        <div className='input-group mb-2'>
                          <span className='input-group-text'>Min</span>
                          <Input
                            type='number'
                            name='dwtMin'
                            id='jettyDwtMin'
                            value={jettyFormData.dwtMin}
                            onChange={handleJettyNumberChange('dwtMin')}
                          />
                          <span className='input-group-text'>MT</span>
                        </div>
                        <div className='input-group'>
                          <span className='input-group-text'>Max</span>
                          <Input
                            type='number'
                            name='dwtMax'
                            id='jettyDwtMax'
                            value={jettyFormData.dwtMax}
                            onChange={handleJettyNumberChange('dwtMax')}
                          />
                          <span className='input-group-text'>MT</span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <div className='mt-3'>
                        <Label className='form-label'>Status</Label>
                        <FormGroup switch className='mb-0'>
                          <Input
                            type='switch'
                            role='switch'
                            id='jettyIsActive'
                            checked={jettyFormData.isActive}
                            onChange={e =>
                              setJettyFormData(prev => ({
                                ...prev,
                                isActive: e.target.checked,
                              }))
                            }
                          />
                          <Label check for='jettyIsActive' className='mb-0'>
                            Actived ?
                          </Label>
                        </FormGroup>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                color='danger'
                className='btn-label btn-sm right'
                onClick={() => setModalJetty(false)}
              >
                {' '}
                <i className=' ri-close-circle-line label-icon align-middle fs-16 ms-2'></i>{' '}
                Closed
              </Button>
              <Button
                color='primary'
                className='btn-label btn-sm right'
                disabled={jettySaving}
                onClick={handleSaveJetty}
              >
                {' '}
                <i className='ri-database-2-line label-icon align-middle fs-16 ms-2'></i>{' '}
                Save
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default JettyLocationList;
